import { api } from './api';

/**
 * The Scratchpad API: a person's private, per-organization named lists and their
 * checkable items. Everything is owner-scoped on the server, so these calls only
 * ever touch the signed-in user's own lists.
 */

/**
 * Every list the caller has in an organization, each with its items ordered.
 * @param {Object} params - { organizationId }
 */
export const getLists = async ({ organizationId }) => {
  const res = await api.get('/lists', { params: { organizationId } });
  return res.data;
};

/**
 * Create a named list in an organization.
 * @param {Object} payload - { organizationId, title, category }
 */
export const createList = async ({ organizationId, title, category }) => {
  const res = await api.post('/lists', { organizationId, title, category });
  return res.data;
};

/**
 * Rename a list.
 * @param {string} listId
 * @param {string} title
 */
export const renameList = async (listId, title) => {
  const res = await api.patch(`/lists/${listId}`, { title });
  return res.data;
};

/**
 * Patch a list — used to move it between category tabs (and can carry a title).
 * @param {string} listId
 * @param {Object} updates - { title, category }
 */
export const updateList = async (listId, updates) => {
  const res = await api.patch(`/lists/${listId}`, updates);
  return res.data;
};

/**
 * Delete a list along with its items.
 * @param {string} listId
 */
export const deleteList = async (listId) => {
  const res = await api.delete(`/lists/${listId}`);
  return res.data;
};

/**
 * Add an item to a list.
 * @param {string} listId
 * @param {string} content
 */
export const addListItem = async (listId, content) => {
  const res = await api.post(`/lists/${listId}/items`, { content });
  return res.data;
};

/**
 * Edit an item's content or check it off.
 * @param {string} itemId
 * @param {Object} updates - { content, done }
 */
export const updateListItem = async (itemId, updates) => {
  const res = await api.patch(`/lists/list-items/${itemId}`, updates);
  return res.data;
};

/**
 * Delete an item.
 * @param {string} itemId
 */
export const deleteListItem = async (itemId) => {
  const res = await api.delete(`/lists/list-items/${itemId}`);
  return res.data;
};

/**
 * Save the order of a list's items.
 * @param {string} listId
 * @param {string[]} orderedItemIds - item ids in the chosen order
 */
export const reorderItems = async (listId, orderedItemIds) => {
  const res = await api.put(`/lists/${listId}/item-order`, { orderedItemIds });
  return res.data;
};

const listsApi = {
  getLists,
  createList,
  renameList,
  updateList,
  deleteList,
  addListItem,
  updateListItem,
  deleteListItem,
  reorderItems,
};

export default listsApi;
